/**
 * ExplorerGrid: virtualized data grid for 200+ rows × 100+ columns.
 *
 * Two-axis virtualization (rows + non-pinned columns) using @tanstack/react-virtual.
 * Sticky header (band + leaf rows), pinned left columns, gutter for row selection.
 * Styled exclusively with @fti/design-system tokens.
 */

import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useTheme, fontFamily, fontSize, fontWeight } from '@fti/design-system';
import type { ExplorerGridProps, ExplorerGridHandle, DisplayColumn } from './contracts';
import { getValue, getGroupValue } from './model/flatten';
import { ColumnHeader } from './ColumnHeader';
import { CellValue } from './CellValue';
import { copyToClipboard } from './format';

/** Group band row height. */
const BAND_H = 20;
/** Leaf header row height; must match ColumnHeader's fixed height. */
const HEADER_H = 72;

export const ExplorerGrid = forwardRef<ExplorerGridHandle, ExplorerGridProps>((props, ref) => {
  const {
    rows,
    indices,
    columns,
    density,
    sort,
    summaries,
    filteredColumns,
    selectedColumn,
    selectedRow,
    highlightedColumn,
    onSort,
    onOpenFilter,
    onSelectColumn,
    onSelectRow,
    onResize,
    onExpandGroup,
  } = props;

  const { theme } = useTheme();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Row height based on density
  const rowHeight = density === 'compact' ? 28 : 36;

  // The group band row only earns its space when some column is nested.
  const hasBands = columns.some((c) => c.band !== null);
  const bandH = hasBands ? BAND_H : 0;
  const headerTotal = bandH + HEADER_H;

  // Separate pinned and center columns
  const { pinnedColumns, centerColumns, gutterWidth } = useMemo(() => {
    const pinned = columns.filter((c) => c.pinned);
    const center = columns.filter((c) => !c.pinned);
    return { pinnedColumns: pinned, centerColumns: center, gutterWidth: 48 };
  }, [columns]);

  // Cumulative widths for pinned columns
  const pinnedOffsets = useMemo(() => {
    let offset = gutterWidth;
    return pinnedColumns.map((col) => {
      const current = offset;
      offset += col.width;
      return current;
    });
  }, [pinnedColumns, gutterWidth]);

  const totalPinnedWidth = pinnedOffsets.length > 0
    ? pinnedOffsets[pinnedOffsets.length - 1] + pinnedColumns[pinnedColumns.length - 1].width
    : gutterWidth;

  // Row virtualizer
  const rowVirtualizer = useVirtualizer({
    count: indices.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => rowHeight,
    overscan: 10,
    // Rows start below the sticky header, so item offsets include its height.
    scrollMargin: headerTotal,
    // Keep keyboard-scrolled rows from landing under the sticky header.
    scrollPaddingStart: headerTotal,
  });

  // Column virtualizer (only for center columns)
  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: centerColumns.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: (i) => centerColumns[i].width,
    overscan: 4,
    // Center columns start after the gutter and pinned columns; without this margin the
    // virtualizer computes the visible range as if they started at the left edge.
    scrollMargin: totalPinnedWidth,
    // …and columns from landing under the pinned area.
    scrollPaddingStart: totalPinnedWidth,
  });

  // Widths come from the layout, not from DOM measurement, so re-measure when they change.
  useEffect(() => {
    columnVirtualizer.measure();
  }, [centerColumns, columnVirtualizer]);
  useEffect(() => {
    rowVirtualizer.measure();
  }, [rowHeight, rowVirtualizer]);

  // Imperative handle
  useImperativeHandle(ref, () => ({
    scrollToColumn: (columnId: string) => {
      const centerIndex = centerColumns.findIndex((c) => c.id === columnId);
      if (centerIndex >= 0) {
        columnVirtualizer.scrollToIndex(centerIndex, { align: 'center' });
      }
    },
    scrollToRow: (rowIndex: number) => {
      const pos = indices.indexOf(rowIndex);
      if (pos >= 0) {
        rowVirtualizer.scrollToIndex(pos, { align: 'auto' });
      }
    },
  }), [centerColumns, columnVirtualizer, rowVirtualizer, indices]);

  // ---------------------------------------------------------------------------
  // Keyboard cell focus. The scroll container keeps DOM focus and points
  // aria-activedescendant at the active cell, because virtualized cells mount and
  // unmount as they scroll and can't reliably hold focus themselves.
  // ---------------------------------------------------------------------------
  const [focusCell, setFocusCell] = useState<{ pos: number; col: number } | null>(null);

  // Keep the focused cell inside the grid when rows are filtered or columns hidden.
  const maxPos = indices.length - 1;
  const maxCol = columns.length - 1;
  const activeCell =
    focusCell && maxPos >= 0 && maxCol >= 0
      ? { pos: Math.min(focusCell.pos, maxPos), col: Math.min(focusCell.col, maxCol) }
      : null;

  const scrollCellIntoView = useCallback(
    (pos: number, col: number) => {
      rowVirtualizer.scrollToIndex(pos, { align: 'auto' });
      const centerIdx = col - pinnedColumns.length;
      if (centerIdx >= 0) columnVirtualizer.scrollToIndex(centerIdx, { align: 'auto' });
    },
    [rowVirtualizer, columnVirtualizer, pinnedColumns.length],
  );

  const moveFocus = useCallback(
    (pos: number, col: number) => {
      const next = { pos: Math.max(0, Math.min(pos, maxPos)), col: Math.max(0, Math.min(col, maxCol)) };
      setFocusCell(next);
      scrollCellIntoView(next.pos, next.col);
    },
    [maxPos, maxCol, scrollCellIntoView],
  );

  const handleGridKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      // Only when the grid itself has focus — never hijack keys from header buttons.
      if (e.target !== e.currentTarget || maxPos < 0 || maxCol < 0) return;
      const cur = activeCell ?? { pos: 0, col: 0 };
      const pageRows = Math.max(1, Math.floor((e.currentTarget.clientHeight - headerTotal) / rowHeight) - 1);
      const mod = e.metaKey || e.ctrlKey;
      let handled = true;
      switch (e.key) {
        case 'ArrowDown': moveFocus(cur.pos + 1, cur.col); break;
        case 'ArrowUp': moveFocus(cur.pos - 1, cur.col); break;
        case 'ArrowRight': moveFocus(cur.pos, cur.col + 1); break;
        case 'ArrowLeft': moveFocus(cur.pos, cur.col - 1); break;
        case 'PageDown': moveFocus(cur.pos + pageRows, cur.col); break;
        case 'PageUp': moveFocus(cur.pos - pageRows, cur.col); break;
        case 'Home': moveFocus(mod ? 0 : cur.pos, 0); break;
        case 'End': moveFocus(mod ? maxPos : cur.pos, maxCol); break;
        case 'Enter': onSelectRow(indices[cur.pos]); break;
        default:
          if (mod && e.key.toLowerCase() === 'c') {
            const col = columns[cur.col];
            const row = rows[indices[cur.pos]];
            void copyToClipboard(col.kind === 'leaf' ? getValue(row, col.column) : getGroupValue(row, col.group));
          } else {
            handled = false;
          }
      }
      if (handled) e.preventDefault();
    },
    [activeCell, maxPos, maxCol, rowHeight, moveFocus, onSelectRow, indices, columns, rows],
  );

  const focusRing = `inset 0 0 0 2px ${theme.colors.primary[500]}`;
  const cellId = (pos: number, col: number) => `explorer-cell-${pos}-${col}`;

  // Column resize state and handlers
  const [resizing, setResizing] = useState<{ columnId: string; startX: number; startWidth: number } | null>(null);

  const handleResizeStart = useCallback((e: React.PointerEvent, col: DisplayColumn) => {
    e.stopPropagation();
    setResizing({ columnId: col.id, startX: e.clientX, startWidth: col.width });
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const handleResizeMove = useCallback((e: React.PointerEvent) => {
    if (!resizing) return;
    const dx = e.clientX - resizing.startX;
    const newWidth = Math.max(60, resizing.startWidth + dx);
    onResize(resizing.columnId, newWidth);
  }, [resizing, onResize]);

  const handleResizeEnd = useCallback((e: React.PointerEvent) => {
    if (!resizing) return;
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    setResizing(null);
  }, [resizing]);

  // Band grouping for header
  const bandGroups = useMemo(() => {
    const groups: Array<{ band: string | null; startIndex: number; span: number; width: number }> = [];
    let current: { band: string | null; startIndex: number; span: number; width: number } | null = null;

    columns.forEach((col, i) => {
      if (col.pinned) {
        // Pinned columns have blank bands
        if (current) {
          groups.push(current);
          current = null;
        }
        groups.push({ band: null, startIndex: i, span: 1, width: col.width });
      } else if (current && current.band === col.band) {
        current.span++;
        current.width += col.width;
      } else {
        if (current) groups.push(current);
        current = { band: col.band, startIndex: i, span: 1, width: col.width };
      }
    });
    if (current) groups.push(current);
    return groups;
  }, [columns]);

  // Styles
  const headerBg = theme.colors.background.paper;
  const borderColor = theme.colors.neutral[200];
  const zebraColor = theme.colors.neutral[50];
  const hoverColor = theme.colors.neutral[100];
  const selectedRowBg = theme.colors.primary[50];
  const selectedColTint = theme.colors.primary[50];
  const highlightedColTint = theme.colors.primary[100];
  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;

  const totalContentWidth = totalPinnedWidth + centerColumns.reduce((sum, c) => sum + c.width, 0);
  // getTotalSize() includes the scrollMargin (band + header height).
  const totalContentHeight = rowVirtualizer.getTotalSize();

  const virtualRows = rowVirtualizer.getVirtualItems();
  const virtualColumns = columnVirtualizer.getVirtualItems();

  return (
    <div
      ref={scrollContainerRef}
      role="grid"
      tabIndex={0}
      onKeyDown={handleGridKeyDown}
      onFocus={(e) => {
        if (e.target === e.currentTarget && !focusCell && indices.length > 0) setFocusCell({ pos: 0, col: 0 });
      }}
      aria-activedescendant={activeCell ? cellId(activeCell.pos, activeCell.col) : undefined}
      aria-rowcount={indices.length + 1}
      aria-colcount={columns.length + 1}
      style={{
        height: '100%',
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'auto',
        position: 'relative',
        fontFamily: fontFamily.sans,
        fontSize: fontSize.sm,
        outline: 'none',
      }}
    >
      <div style={{ width: totalContentWidth, height: totalContentHeight, position: 'relative' }}>
        {/* Header: Band row */}
        {hasBands && (
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 3,
            height: 20,
            display: 'flex',
            background: headerBg,
            borderBottom: `1px solid ${borderColor}`,
          }}
        >
          {/* Corner cell above gutter */}
          <div
            style={{
              width: gutterWidth,
              boxSizing: 'border-box',
              height: 20,
              position: 'sticky',
              left: 0,
              zIndex: 4,
              background: headerBg,
              borderRight: `1px solid ${borderColor}`,
            }}
          />

          {/* Band cells for pinned columns */}
          {pinnedColumns.map((col, i) => (
            <div
              key={col.id}
              style={{
                width: col.width,
                boxSizing: 'border-box',
                height: 20,
                position: 'sticky',
                left: pinnedOffsets[i],
                zIndex: 4,
                background: headerBg,
                borderRight: i === pinnedColumns.length - 1 ? `1px solid ${borderColor}` : `1px solid ${borderColor}`,
              }}
            />
          ))}

          {/* Band cells for center columns */}
          <div style={{ position: 'relative', height: 20, flex: 1 }}>
            {bandGroups
              .filter((g) => !columns[g.startIndex].pinned && g.band)
              .map((g, i) => {
                const leftOffset = columns
                  .slice(0, g.startIndex)
                  .filter((c) => !c.pinned)
                  .reduce((sum, c) => sum + c.width, 0);
                return (
                  <div
                    key={`${g.band}-${i}`}
                    style={{
                      position: 'absolute',
                      left: leftOffset,
                      top: 0,
                      width: g.width,
                      boxSizing: 'border-box',
                      height: 20,
                      background: theme.colors.secondary[50],
                      borderLeft: leftOffset > 0 ? `1px solid ${borderColor}` : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: fontSize.xs,
                      fontWeight: fontWeight.semibold,
                      color: textColor,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      padding: '0 4px',
                    }}
                  >
                    {g.band}
                  </div>
                );
              })}
          </div>
        </div>
        )}

        {/* Header: Column header row */}
        <div
          style={{
            position: 'sticky',
            top: bandH,
            zIndex: 3,
            height: HEADER_H,
            display: 'flex',
            background: headerBg,
            borderBottom: `1px solid ${borderColor}`,
          }}
        >
          {/* Gutter column header (blank) */}
          <div
            style={{
              width: gutterWidth,
              boxSizing: 'border-box',
              height: HEADER_H,
              position: 'sticky',
              left: 0,
              zIndex: 4,
              background: headerBg,
              borderRight: `1px solid ${borderColor}`,
            }}
          />

          {/* Pinned column headers */}
          {pinnedColumns.map((col, i) => {
            const isLast = i === pinnedColumns.length - 1;
            return (
              <div
                key={col.id}
                style={{
                  width: col.width,
                  boxSizing: 'border-box',
                  height: HEADER_H,
                  position: 'sticky',
                  left: pinnedOffsets[i],
                  zIndex: 4,
                  background: headerBg,
                  borderRight: `1px solid ${borderColor}`,
                  boxShadow: isLast ? '2px 0 4px rgba(0,0,0,0.1)' : undefined,
                }}
              >
                <ColumnHeader
                  column={col}
                  summary={summaries.get(col.id)}
                  sortDirection={sort?.column === col.id ? sort.direction : null}
                  filtered={filteredColumns.has(col.id)}
                  selected={selectedColumn === col.id}
                  onSort={() => onSort(col.id)}
                  onOpenFilter={(anchor) => onOpenFilter(col.id, anchor)}
                  onSelect={() => onSelectColumn(col.id)}
                  onExpandGroup={col.kind === 'group' ? () => onExpandGroup(col.id) : undefined}
                />
                {/* Resize handle */}
                <div
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: 6,
                    boxSizing: 'border-box',
                    cursor: 'col-resize',
                    zIndex: 5,
                  }}
                  onPointerDown={(e) => handleResizeStart(e, col)}
                  onPointerMove={handleResizeMove}
                  onPointerUp={handleResizeEnd}
                />
              </div>
            );
          })}

          {/* Center column headers (virtualized) */}
          <div style={{ position: 'relative', height: HEADER_H, flex: 1 }}>
            {virtualColumns.map((vc) => {
              const col = centerColumns[vc.index];
              return (
                <div
                  key={col.id}
                  style={{
                    position: 'absolute',
                    left: vc.start - totalPinnedWidth,
                    top: 0,
                    width: col.width,
                    boxSizing: 'border-box',
                    height: HEADER_H,
                    background: headerBg,
                    borderRight: `1px solid ${borderColor}`,
                  }}
                >
                  <ColumnHeader
                    column={col}
                    summary={summaries.get(col.id)}
                    sortDirection={sort?.column === col.id ? sort.direction : null}
                    filtered={filteredColumns.has(col.id)}
                    selected={selectedColumn === col.id}
                    onSort={() => onSort(col.id)}
                    onOpenFilter={(anchor) => onOpenFilter(col.id, anchor)}
                    onSelect={() => onSelectColumn(col.id)}
                    onExpandGroup={col.kind === 'group' ? () => onExpandGroup(col.id) : undefined}
                  />
                  {/* Resize handle */}
                  <div
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: 6,
                      boxSizing: 'border-box',
                      cursor: 'col-resize',
                      zIndex: 5,
                    }}
                    onPointerDown={(e) => handleResizeStart(e, col)}
                    onPointerMove={handleResizeMove}
                    onPointerUp={handleResizeEnd}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Rows */}
        {indices.length === 0 ? (
          <div
            style={{
              position: 'absolute',
              top: headerTotal,
              left: 0,
              right: 0,
              padding: 32,
              textAlign: 'center',
              color: mutedColor,
              fontSize: fontSize.base,
            }}
          >
            No rows match the current filters.
          </div>
        ) : (
          virtualRows.map((vr) => {
            const rowIndex = indices[vr.index];
            const row = rows[rowIndex];
            const isSelected = selectedRow === rowIndex;
            const isEven = vr.index % 2 === 0;

            return (
              <div
                key={vr.index}
                role="row"
                aria-rowindex={vr.index + 2}
                style={{
                  position: 'absolute',
                  top: vr.start,
                  left: 0,
                  width: totalContentWidth,
                  boxSizing: 'border-box',
                  height: rowHeight,
                  display: 'flex',
                  background: isSelected ? selectedRowBg : isEven ? zebraColor : 'transparent',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) (e.currentTarget as HTMLElement).style.background = hoverColor;
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) (e.currentTarget as HTMLElement).style.background = isEven ? zebraColor : 'transparent';
                }}
                onDoubleClick={() => onSelectRow(rowIndex)}
              >
                {/* Gutter cell */}
                <button
                  role="gridcell"
                  style={{
                    width: gutterWidth,
                    boxSizing: 'border-box',
                    height: rowHeight,
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    background: isSelected ? selectedRowBg : isEven ? zebraColor : theme.colors.background.default,
                    border: 'none',
                    borderRight: `1px solid ${borderColor}`,
                    borderBottom: `1px solid ${borderColor}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: fontSize.xs,
                    color: mutedColor,
                    cursor: 'pointer',
                    fontFamily: fontFamily.sans,
                  }}
                  onClick={() => onSelectRow(rowIndex)}
                >
                  {vr.index + 1}
                </button>

                {/* Pinned cells */}
                {pinnedColumns.map((col, i) => {
                  const value = col.kind === 'leaf' ? getValue(row, col.column) : getGroupValue(row, col.group);
                  const type = col.kind === 'leaf' ? col.column.type : 'object';
                  const colSelected = selectedColumn === col.id;
                  const colHighlighted = highlightedColumn === col.id;
                  const cellBg = isSelected
                    ? selectedRowBg
                    : colHighlighted
                    ? highlightedColTint
                    : colSelected
                    ? selectedColTint
                    : isEven
                    ? zebraColor
                    : theme.colors.background.default;

                  return (
                    <div
                      key={col.id}
                      id={cellId(vr.index, i)}
                      role="gridcell"
                      onClick={() => {
                        setFocusCell({ pos: vr.index, col: i });
                        scrollContainerRef.current?.focus({ preventScroll: true });
                      }}
                      style={{
                        width: col.width,
                        boxSizing: 'border-box',
                        height: rowHeight,
                        position: 'sticky',
                        left: pinnedOffsets[i],
                        zIndex: 2,
                        background: cellBg,
                        boxShadow: activeCell?.pos === vr.index && activeCell.col === i ? focusRing : undefined,
                        borderRight: `1px solid ${borderColor}`,
                        borderBottom: `1px solid ${borderColor}`,
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 6px',
                        overflow: 'hidden',
                      }}
                    >
                      <CellValue value={value} type={type} />
                    </div>
                  );
                })}

                {/* Center cells (virtualized) */}
                <div style={{ position: 'relative', flex: 1, height: rowHeight }}>
                  {virtualColumns.map((vc) => {
                    const col = centerColumns[vc.index];
                    const value = col.kind === 'leaf' ? getValue(row, col.column) : getGroupValue(row, col.group);
                    const type = col.kind === 'leaf' ? col.column.type : 'object';
                    const colSelected = selectedColumn === col.id;
                    const colHighlighted = highlightedColumn === col.id;
                    const cellBg = isSelected
                      ? selectedRowBg
                      : colHighlighted
                      ? highlightedColTint
                      : colSelected
                      ? selectedColTint
                      : 'transparent';

                    return (
                      <div
                        key={col.id}
                        id={cellId(vr.index, pinnedColumns.length + vc.index)}
                        role="gridcell"
                        onClick={() => {
                          setFocusCell({ pos: vr.index, col: pinnedColumns.length + vc.index });
                          scrollContainerRef.current?.focus({ preventScroll: true });
                        }}
                        style={{
                          position: 'absolute',
                          left: vc.start - totalPinnedWidth,
                          top: 0,
                          width: col.width,
                          boxSizing: 'border-box',
                          height: rowHeight,
                          background: cellBg,
                          boxShadow:
                            activeCell?.pos === vr.index && activeCell.col === pinnedColumns.length + vc.index
                              ? focusRing
                              : undefined,
                          borderRight: `1px solid ${borderColor}`,
                          borderBottom: `1px solid ${borderColor}`,
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0 6px',
                          overflow: 'hidden',
                        }}
                      >
                        <CellValue value={value} type={type} />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
});

ExplorerGrid.displayName = 'ExplorerGrid';
